import { ReactComponent as AllProductsIcon } from "@assets/icons/sidebar/ic_small_bag.svg";
import { zodResolver } from "@hookform/resolvers/zod";
import { performImport } from "@redux/features/dfs/thunks";
import { useImportShopifyDataMutation } from "@redux/features/integrations/shopifyApi";
import { useAppDispatch } from "@redux/hooks";
import BaseButton from "@reusables/BaseButton";
import BaseModal from "@reusables/Modals/BaseModal";
import { Control, Controller, useForm, useWatch } from "react-hook-form";
import { z } from "zod";
import { useTranslation } from "react-i18next";
import SelectableImportCategory from "../SelectableImportCategory";
import LocationsDisplayer from "./LocationsDisplayer";
import LocationsMapper from "./LocationsMapper/index";

interface ShopifyImportModalProps {
    isOpen: boolean;
    onClose: () => void;
}

const formSchema = z.object({
    shouldImportProducts: z.boolean(),
});

export type ShopifyImportFormTyping = z.infer<typeof formSchema>;

export default function ShopifyImportModal({ isOpen, onClose }: ShopifyImportModalProps) {
    const { t } = useTranslation("", { keyPrefix: "imports.modules.shopify.importModal" });
    const dispatch = useAppDispatch();

    const [importShopify, { isLoading: isImportLoading }] = useImportShopifyDataMutation();

    const form = useForm<ShopifyImportFormTyping>({
        resolver: zodResolver(formSchema),
        defaultValues: {
            shouldImportProducts: false,
        },
    });

    const onSubmit = form.handleSubmit((data) => {
        dispatch(
            performImport("shopify", () => {
                const entities: "product"[] = [];

                if (data.shouldImportProducts) {
                    entities.push("product");
                }

                return importShopify({
                    entities: entities,
                }).unwrap();
            })
        );

        form.reset();
        onClose();
    }, console.error);

    return (
        <BaseModal
            isOpen={isOpen}
            onClose={onClose}
            width={800}
            padding="45px"
            useCloseIcon
            isLoading={isImportLoading}
        >
            <form onSubmit={onSubmit}>
                <div className="text-xl font-semibold text-center text-accent mb-4">
                    {t("heading")}
                </div>

                <div className="space-y-4">
                    <div className="text-[#686868] font-thin text-sm">{t("description")}.</div>

                    {/* Usage & import guidelines */}
                    <div className="space-y-2 text-accent text-sm">
                        <div className="font-semibold text-inputs-label-dim">
                            {t("rules.label")}
                        </div>
                        <ul className="list-disc ml-4 space-y-1">
                            <li>{t("rules.sync")}</li>
                            <li>{t("rules.locationMapping")}</li>
                        </ul>
                    </div>

                    <LocationsDisplayer />
                    <LocationsMapper />

                    <div className="space-y-2">
                        <div className="font-thin text-inputs-label-dim text-sm">
                            {t("importSelection.label")}:
                        </div>
                        <div className="text-accent">
                            <Controller
                                name="shouldImportProducts"
                                control={form.control}
                                render={({ field }) => (
                                    <SelectableImportCategory
                                        isSelected={field.value}
                                        onChange={field.onChange}
                                        text={t("entities.products")}
                                        icon={<AllProductsIcon width={22} height={22} />}
                                    />
                                )}
                            />
                        </div>
                    </div>
                </div>

                <div className="mt-8 grid grid-cols-2 gap-2">
                    <BaseButton
                        buttonWidth="100%"
                        text={t("buttons.cancel")}
                        size="md"
                        type="button"
                        primaryOutlined
                        onClick={onClose}
                    />
                    <ImportButtonDisabler control={form.control}>
                        {(allowImport) => (
                            <BaseButton
                                buttonWidth="100%"
                                text={t("buttons.import")}
                                size="md"
                                type="submit"
                                disabled={!allowImport}
                            />
                        )}
                    </ImportButtonDisabler>
                </div>
            </form>
        </BaseModal>
    );
}

function ImportButtonDisabler({
    control,
    children,
}: {
    control: Control<ShopifyImportFormTyping>;
    children: (allowImport: boolean) => JSX.Element;
}) {
    const shouldImportProducts = useWatch({ control, name: "shouldImportProducts" });

    const allowImport = shouldImportProducts;

    return children(allowImport);
}
